/**
 * Key-role backfill API client.
 *
 * Wraps the assayer-backend `/admin/key-roles/*` endpoints which drive
 * Cynapse person-resolution server-side (VPC-internal) for the FR
 * mig 147 canonical-Person backfill.
 */
import { apiClient } from './config.js';
import { waitForToolRun, waitForMaintenanceJob, type ToolRunStatus, type MaintenanceJobStatus } from './tools.js';

export interface BackfillKeyRolePersonResult {
  stats: {
    processed: number;
    bound: number;
    no_match: number;
    error: number;
    skipped: number;
  };
  examples: {
    bound: Array<{ binding_id: string; full_name: string; person_entity_id?: string; would_bind_to_person_entity_id?: string; was_created?: boolean }>;
    no_match: Array<{ binding_id: string; full_name: string; cynapse_company_entity_id?: string | null }>;
    error: Array<{ binding_id: string; full_name: string; error?: string }>;
  };
  error?: string;
}

export interface KeyRolePersonBackfillProgress {
  total: number;
  bound: number;
  unbound: number;
  unbound_resolved: number;
  unbound_pending: number;
  unbound_unresolvable: number;
}

export async function backfillKeyRolePersonBindings(params?: {
  dry_run?: boolean;
  limit?: number;
}): Promise<BackfillKeyRolePersonResult> {
  const { data } = await apiClient.post<BackfillKeyRolePersonResult>(
    '/admin/key-roles/backfill-person-resolve',
    null,
    { params },
  );
  return data;
}

export async function getKeyRolePersonBackfillProgress(): Promise<KeyRolePersonBackfillProgress> {
  const { data } = await apiClient.get<KeyRolePersonBackfillProgress>(
    '/admin/key-roles/backfill-person-resolve-progress',
  );
  return data;
}

// NOTE: the selective DIN backfill client was RETIRED 2026-08-13 — DIN is not
// on ASX documents (needs an external lookup). Company identifiers (ACN/ARSN)
// replaced it; the filer path resolves inline server-side.


// ---------------------------------------------------------------------------
// Person/Org Resolver step 5 — key-role succession (2026-09-17)
// ---------------------------------------------------------------------------

export interface KeyRoleSuccessionClosure {
  binding_id: string;
  person_key: string;
  role: string;
  effective_to: string;
  superseded_by: string | null;
  rule: string;
  reason: string;
}

export interface KeyRoleSuccessionEntityResult {
  entity_id: string;
  closures: KeyRoleSuccessionClosure[];
  skips: { role: string; person_key: string; reason: string; detail?: Record<string, unknown> }[];
  applied: number;
  [k: string]: unknown;
}

export interface KeyRoleSuccessionResult {
  entities: number;
  closures: number;
  applied: number;
  skips: number;
  by_rule: Record<string, number>;
  skip_reasons: Record<string, number>;
  dry_run: boolean;
  results: KeyRoleSuccessionEntityResult[];
}

/** Async (a full sweep passes the gateway's 60 s cut): submits, then polls to completion. */
export async function runKeyRoleSuccession(params: {
  dry_run: boolean;
  codes?: string;
  limit?: number;
}, onProgress?: (s: ToolRunStatus) => void): Promise<KeyRoleSuccessionResult> {
  const q: Record<string, string | number | boolean> = { dry_run: params.dry_run };
  if (params.codes) q.codes = params.codes;
  if (params.limit) q.limit = params.limit;
  const { data } = await apiClient.post<{ run_id: string }>('/tools/key-role-succession', null, { params: q });
  return waitForToolRun<KeyRoleSuccessionResult>(data.run_id, onProgress);
}


// ---------------------------------------------------------------------------
// Person embedding heal (2026-09-20) — persons with no name vector are
// invisible to the resolver's embedding tiers and get re-created by every
// later filing. Scoped to persons our bindings reference; capped; metered.
// ---------------------------------------------------------------------------

export interface HealPersonEmbeddingsResult {
  dry_run: boolean;
  scope: 'referenced' | 'all';
  unembedded_total: number | null;
  referenced: number | null;
  candidates: number;
  chars: number;
  batches: number;
  healed: number;
  failed: number;
  model?: string | null;
}

/** Synchronous — a 500-row run is ~20 Vertex calls. */
export async function healPersonEmbeddings(params: {
  dry_run: boolean;
  limit?: number;
  scope?: 'referenced' | 'all';
}): Promise<HealPersonEmbeddingsResult> {
  const { data } = await apiClient.post<HealPersonEmbeddingsResult>(
    '/tools/heal-person-embeddings',
    null,
    { params },
  );
  return data;
}


// ---------------------------------------------------------------------------
// Person role-history backfill (2026-09-22) — replays key-role bindings into
// Cynapse person_role_history so resolver tiers 2/3 (name + employer) fire.
// Database-only, idempotent; one chunk of whole issuers per call.
// ---------------------------------------------------------------------------

export interface PersonRoleHistoryResult {
  dry_run: boolean;
  chunks: number;
  issuers: number;
  bindings: number;
  counts: Record<string, number>;
}

/** Async: submits one run over every issuer, then polls; progress carries chunks/issuers/bindings/counts. */
export async function backfillPersonRoleHistory(params: { dry_run: boolean; codes?: string },
  onProgress?: (s: MaintenanceJobStatus) => void): Promise<PersonRoleHistoryResult> {
  const { data } = await apiClient.post<{ job_id: string }>('/tools/backfill-person-role-history', null, { params });
  return waitForMaintenanceJob<PersonRoleHistoryResult>(data.job_id, onProgress);
}


// ---------------------------------------------------------------------------
// Person ↔ contact link + merge-queue regate (2026-09-22). One identity
// surfaced, nothing deleted; every pair is name-gated in Cynapse.
// ---------------------------------------------------------------------------

export interface LinkPersonContactsResult {
  dry_run: boolean;
  keys: 'A' | 'B' | 'AB';
  pairs_in: number;
  filings_in: number;
  counts: Record<string, number>;
}

/** Async: submits one run over every binding, then polls; progress carries offset/total. Database-only. */
export async function linkPersonContacts(params: { dry_run: boolean; keys?: 'A' | 'B' | 'AB' },
  onProgress?: (s: MaintenanceJobStatus) => void): Promise<LinkPersonContactsResult> {
  const { data } = await apiClient.post<{ job_id: string }>('/tools/link-person-contacts', null, { params });
  return waitForMaintenanceJob<LinkPersonContactsResult>(data.job_id, onProgress);
}

export interface RegateMergeCandidatesResult {
  dry_run: boolean;
  checked: number;
  failing: number;
  expired: number;
  examples: { incoming: string; matched: string }[];
  /** Active contact→person links re-checked by the same gate; failing ones are ended, not deleted. */
  links?: { checked: number; failing: number; ended: number; examples: { contact: string; person: string }[] };
}

export interface FirmHygieneMember { entity_id: string; name: string | null; domain: string | null; edges: number }
export interface FirmHygieneResult {
  dry_run: boolean;
  firms_considered: number;
  duplicates_found: number;
  queued: number;
  skipped_already_pending: number;
  skipped_rejected_before: number;
  groups: {
    survivor: FirmHygieneMember;
    duplicates: (FirmHygieneMember & {
      reason: string;
      action: 'would_queue' | 'queued' | 'already_pending' | 'rejected_before';
      /** Exact duplicate (same normalised name, or only the survivor's email domain): merged without review. */
      exact?: boolean;
      auto_merge?: boolean;
      auto_action?: string;
    })[];
  }[];
  market_operator_edges: { found: number; ended: number; by_firm: Record<string, number> };
  auto_merged?: number;
  auto_refused?: number;
  would_auto_merge?: number;
}

export interface IrPrEdgeRepairResult {
  dry_run: boolean;
  lines: number;
  filings: number;
  filings_mapped: number;
  pairs: number;
  edges_found: number;
  pairs_without_edge: number;
  upgraded: number;
  firms: { firm: string; upgraded: number }[];
}

/** Relabel IR/PR firm→client edges from their contact lines (durable job, Cynapse 0.19.6). */
export async function runIrPrEdgeRepair(params: { dry_run: boolean },
  onProgress?: (s: MaintenanceJobStatus) => void): Promise<IrPrEdgeRepairResult> {
  const { data } = await apiClient.post<{ job_id: string }>('/tools/ir-pr-edge-repair', null, { params });
  return waitForMaintenanceJob<IrPrEdgeRepairResult>(data.job_id, onProgress);
}

/** Queues a durable maintenance job and waits for it (a deploy mid-run re-queues it). */
export async function runFirmHygiene(params: { dry_run: boolean },
  onProgress?: (s: MaintenanceJobStatus) => void): Promise<FirmHygieneResult> {
  const { data } = await apiClient.post<{ job_id: string }>('/tools/firm-hygiene', null, { params });
  return waitForMaintenanceJob<FirmHygieneResult>(data.job_id, onProgress);
}

export async function regateMergeCandidates(params: { dry_run: boolean }): Promise<RegateMergeCandidatesResult> {
  const { data } = await apiClient.post<RegateMergeCandidatesResult>('/tools/regate-merge-candidates', null, { params });
  return data;
}


// ---------------------------------------------------------------------------
// Who works where (2026-09-22, relationship layer M5a): the firm on every
// announcement contact line + an employed_by sighting dated by the filing.
// ---------------------------------------------------------------------------

export interface ContactEmploymentResult {
  dry_run: boolean;
  filings: number;
  counts: Record<string, number>;
}

/** Async: submits one run over every filing with contact lines, then polls. */
export async function backfillContactEmployment(params: { dry_run: boolean },
  onProgress?: (s: MaintenanceJobStatus) => void): Promise<ContactEmploymentResult> {
  const { data } = await apiClient.post<{ job_id: string }>('/tools/backfill-contact-employment', null, { params });
  return waitForMaintenanceJob<ContactEmploymentResult>(data.job_id, onProgress);
}


export interface RelationshipEdgesResult {
  dry_run: boolean;
  edges: number;
  counts: Record<string, number>;
}

/** Async: promotes filing mentions + the M&A adviser field into dated edges. */
export async function backfillRelationshipEdges(params: { dry_run: boolean },
  onProgress?: (s: MaintenanceJobStatus) => void): Promise<RelationshipEdgesResult> {
  const { data } = await apiClient.post<{ job_id: string }>('/tools/backfill-relationship-edges', null, { params });
  return waitForMaintenanceJob<RelationshipEdgesResult>(data.job_id, onProgress);
}


export interface CollapseIdentitiesResult {
  dry_run: boolean;
  groups: number;
  total_groups: number;
  skipped: Record<string, number>;
  merged: number;
  persons_removed: number;
  bindings_rebound: number;
  failed: number;
  examples: { keep: string; names: string[]; evidence: string }[];
}

/** Async: merges person rows whose sources agree (ruling R9). */
export async function collapseIdentities(params: { dry_run: boolean; limit?: number },
  onProgress?: (s: MaintenanceJobStatus) => void): Promise<CollapseIdentitiesResult> {
  const { data } = await apiClient.post<{ job_id: string }>('/tools/collapse-identities', null, { params });
  return waitForMaintenanceJob<CollapseIdentitiesResult>(data.job_id, onProgress);
}
